import {
    createEurekaPageViewReporter,
    createTrackingTime,
    type PageViewSnapshot,
    type TrackingTimeTracker,
} from '@patsnap/tracking-time';
import { psAxios } from '@patsnap/common-ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Logger } from '@patsnap/tahiti-core';
import { EUREKA_SERVICE_URI } from '@telecom/shared/domain-config/config';
import {
    RouterService,
    type RouterService as RouterServiceType,
} from '@telecom/shared/services/router';
import { getAgentNameByRoute } from '@telecom/shared/utils/agent-name';
import type { TrackingTimeService } from '../tracking-time-service';

const DRAFTING_PRODUCT_LINE = 'eureka_ip';

function getRouterLocation(url: string): string | null {
    try {
        const parsedUrl = new URL(url);
        if (parsedUrl.hash.startsWith('#/')) {
            return parsedUrl.hash.slice(1);
        }

        return `${parsedUrl.pathname}${parsedUrl.search}`;
    } catch {
        return null;
    }
}

function normalizeRouteTemplate(path: string): string {
    if (path === '/') return path;

    const segments = path
        .split('/')
        .filter(Boolean)
        .map((segment) => (segment.startsWith(':') ? ':id' : segment));

    return segments.length ? `/${segments.join('/')}` : 'unknown';
}

/**
 * 根据完整 URL 查询 Drafting 路由信息。
 * 页面名称和上报页面路径由 SDK 分别调用对应的标准化函数。
 */
function getDraftingTrackingPageInfo(
    router: Pick<RouterServiceType, 'resolve'>,
    url: string,
): { page_name: string; page_path: string } {
    const routerLocation = getRouterLocation(url);
    if (!routerLocation) {
        return {
            page_name: 'unknown',
            page_path: 'unknown',
        };
    }

    const route = router.resolve(routerLocation);
    if (typeof route.name !== 'string' || !route.name || route.matched.length === 0) {
        return {
            page_name: 'unknown',
            page_path: 'unknown',
        };
    }

    const matchedRoute = route.matched[route.matched.length - 1];

    return {
        page_name: route.name,
        page_path: normalizeRouteTemplate(matchedRoute.path),
    };
}

/** 根据完整 URL 生成 Drafting 的稳定页面名称。 */
export function sanitizeDraftingPageName(
    router: Pick<RouterServiceType, 'resolve'>,
    url: string,
): string {
    return getDraftingTrackingPageInfo(router, url).page_name;
}

/** 根据完整 URL 生成 Drafting 的脱敏页面路径。 */
export function sanitizeDraftingPagePath(
    router: Pick<RouterServiceType, 'resolve'>,
    url: string,
): string {
    return getDraftingTrackingPageInfo(router, url).page_path;
}

/** drafting 页面参与度统计服务实现。 */
@injectable()
export class TrackingTimeServiceImpl implements TrackingTimeService {
    private _tracker: TrackingTimeTracker | null = null;
    private _disposeRouteListener: (() => void) | null = null;
    /** 最近一次已开始统计的完整页面地址，用于过滤路由器对同一地址的重复通知。 */
    private _currentPageFullPath: string | null = null;

    constructor(
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(RouterService)
        private readonly _routerService: RouterServiceType,
    ) {}

    /** 初始化页面参与度统计 SDK。 */
    initialize(): void {
        if (this._tracker) return;

        try {
            this._tracker = createTrackingTime({
                reportPageView: createEurekaPageViewReporter({
                    axios: psAxios,
                    baseUrl: EUREKA_SERVICE_URI,
                    getProductLine: () => DRAFTING_PRODUCT_LINE,
                    getAgentName: (snapshot: Readonly<PageViewSnapshot>) =>
                        getAgentNameByRoute(snapshot.page_full_path),
                }),
                sanitizePageName: (url) =>
                    sanitizeDraftingPageName(this._routerService, url),
                sanitizePagePath: (url) =>
                    sanitizeDraftingPagePath(this._routerService, url),
                autoTrackRouteChanges: false,
                onError: (error) => {
                    this._logger.warn('[TrackingTime] 页面浏览上报失败', error);
                },
            });
            this._currentPageFullPath = window.location.href;
            this._disposeRouteListener = this._routerService.afterEach(() => {
                const nextPageFullPath = window.location.href;
                if (nextPageFullPath === this._currentPageFullPath) return;

                this._currentPageFullPath = nextPageFullPath;
                this._tracker?.restartPageView();
            });
        } catch (error) {
            this._logger.warn('[TrackingTime] 初始化失败', error);
        }
    }

    /** 销毁页面参与度统计 SDK。 */
    async dispose(): Promise<void> {
        this._disposeRouteListener?.();
        this._disposeRouteListener = null;
        this._currentPageFullPath = null;
        const tracker = this._tracker;
        this._tracker = null;
        if (!tracker) return;

        try {
            await tracker.destroy();
        } catch (error) {
            this._logger.warn('[TrackingTime] 销毁失败', error);
        }
    }
}
